import { useGetBuilding } from "api/hooks/allHooks";
import { useCallback, useEffect, useMemo } from "react";

import type { CrremPathwayViewSettings } from "../model/types";

import { m2 } from "../../../utils/textSymbols";
import { buildCorrectionOverlayLines, scaleStackSeries, toYearlyPoints } from "../lib/buildCorrectionStacks";
import { buildPartitionedStacks } from "../lib/buildPartitionedStacks";
import { collectChartYears } from "../lib/collectPathwayYears";
import { formatCorrectionLabel } from "../lib/formatScenarioLabel";
import { parseBuildingAreas } from "../lib/parseBuildingAreas";
import {
  dashboardMetricUnit,
  hasCrremDashboardData,
  pickDashboardMetric,
  pickPathwayTargets,
  pickTrendScenarios,
  toPathwayScenarios,
} from "../lib/parseCrremDashboardApi";
import { resolveAreaDivisor, scaleChartValue } from "../lib/scaleChartValues";
import { isBaselineCorrections } from "../model/constants";
import { useCrremDashboardScenarios } from "./useCrremDashboardScenarios";
import { useCrremPathwaySettings } from "./useCrremPathwaySettings";

export const useCrremPathwayChart = (projectId: string, fallbackUnit: string) => {
  const { config, saveSettings, settings, updateConfig } = useCrremPathwaySettings(projectId);
  const { displayMetric, partitionBy, scale, scenarioKey, visibleCorrectionIds } = config;

  const updateView = useCallback(
    (change: Partial<CrremPathwayViewSettings>) => {
      updateConfig(change);
    },
    [updateConfig]
  );

  const dashboardQuery = useCrremDashboardScenarios(projectId);
  const buildingQuery = useGetBuilding(projectId);
  const isLoading = dashboardQuery.isLoading || buildingQuery.isLoading;

  const chartData = useMemo(() => {
    const dashboardScenarios = pickTrendScenarios(dashboardQuery.data, settings.trendMethod);
    const baselineScenario = dashboardScenarios.find((scenario) => isBaselineCorrections(scenario.corrections));
    const correctionScenarios = dashboardScenarios.filter(
      (scenario) => !isBaselineCorrections(scenario.corrections)
    );
    const visibleCorrections = correctionScenarios.filter((scenario) =>
      visibleCorrectionIds.includes(scenario.id)
    );
    const areas = parseBuildingAreas(buildingQuery.data);
    const divisor = resolveAreaDivisor(scale, areas);
    const scenarios = toPathwayScenarios(pickPathwayTargets(dashboardQuery.data, displayMetric));
    const selectedScenario = scenarios.find((scenario) => scenario.key === scenarioKey) ?? scenarios[0];
    const baselineRows = baselineScenario ? pickDashboardMetric(baselineScenario, displayMetric).rows : [];
    const baseline = toYearlyPoints(baselineRows).map((point) => ({
      ...point,
      value: scaleChartValue(point.value, divisor),
    }));
    const { series: rawStacks, years: stackYears } = buildPartitionedStacks(baselineRows, partitionBy);
    const overlayLines = buildCorrectionOverlayLines(visibleCorrections, displayMetric, divisor);
    const years = collectChartYears(
      [baseline, selectedScenario?.points ?? [], ...overlayLines.map((line) => line.points)],
      stackYears
    );
    const unit = dashboardMetricUnit(baselineScenario ?? correctionScenarios[0], displayMetric, fallbackUnit);

    return {
      areaDivisor: divisor,
      baseline,
      correctionOptions: correctionScenarios.map((scenario) => ({
        corrections: scenario.corrections,
        id: scenario.id,
        label: formatCorrectionLabel(scenario.corrections, scenario.label),
      })),
      grossArea: areas.grossArea,
      overlayLines,
      pathway: (selectedScenario?.points ?? []).map((point) => ({
        ...point,
        value: scaleChartValue(point.value, divisor, areas.grossArea),
      })),
      scenarios,
      selectedScenarioKey: selectedScenario?.key,
      stacks: scaleStackSeries(rawStacks, stackYears, years, divisor),
      unit: divisor ? `${unit}/${m2}` : unit,
      years,
    };
  }, [
    buildingQuery.data,
    dashboardQuery.data,
    displayMetric,
    fallbackUnit,
    partitionBy,
    scale,
    scenarioKey,
    settings.trendMethod,
    visibleCorrectionIds,
  ]);

  useEffect(() => {
    if (chartData.selectedScenarioKey && chartData.selectedScenarioKey !== scenarioKey) {
      updateConfig({ scenarioKey: chartData.selectedScenarioKey });
    }
  }, [chartData.selectedScenarioKey, scenarioKey, updateConfig]);

  const setScenarioKey = useCallback((key: string) => updateConfig({ scenarioKey: key }), [updateConfig]);

  return {
    ...chartData,
    displayMetric,
    isEmpty: !isLoading && !hasCrremDashboardData(dashboardQuery.data),
    isError: dashboardQuery.isError,
    isLoading,
    missing: dashboardQuery.data?.missing ?? [],
    partitionBy,
    saveSettings,
    scale,
    selectedScenarioKey: chartData.selectedScenarioKey,
    setScenarioKey,
    settings,
    updateView,
    visibleCorrectionIds,
  };
};
